/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Button,
  useDisclosure,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Grid,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { FunnelSimple, GridFour, Table } from 'phosphor-react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import SelectInputForm from '../../../../../components/inputs/forms/SelectInputForm';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../../components/SearchModeBar';
import { FilterDrawer } from '../../../../../components/FilterDrawer';
import { ISelect } from '../../../../../types/shared';
import { ICompany, useCompanies } from '../../../../../hooks/useCompanies';
import { IClient } from '../../../../../hooks/useClients';
import { getFormattedLabelString } from '../../../../../utils/FormatFunctions';

interface YearNavigatorProps {
  currentYear: number;
  onPrevYear: () => void;
  onNextYear: () => void;
  onSelectYear: (year: number) => void;
}

function YearNavigator({ currentYear, onPrevYear, onNextYear, onSelectYear }: YearNavigatorProps) {
  const isCurrentYear = currentYear === moment().year();
  const [popoverYear, setPopoverYear] = useState(Math.floor(currentYear / 10) * 10);
  const currentYearNow = moment().year();

  const startYear = popoverYear;
  const years = Array.from({ length: 12 }, (_, i) => startYear + i);

  const handleDecadeChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setPopoverYear(prev => prev - 10);
    } else if (direction === 'next') {
      setPopoverYear(prev => prev + 10);
    }
  };

  const isYearDisabled = (year: number) => year > currentYearNow;
  const isYearSelected = (year: number) => year === currentYear;

  return (
    <HStack spacing={2} bg="white" px={4} borderRadius="lg" shadow="sm" height="38px">
      <IconButton
        aria-label="Ano anterior"
        icon={<Icon as={ChevronLeft} />}
        onClick={onPrevYear}
        variant="ghost"
        size="sm"
        colorScheme="blue"
      />
      <Popover placement="bottom">
        <PopoverTrigger>
          <HStack
            spacing={2}
            minW="120px"
            justify="center"
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            px={2}
            py={1}
            borderRadius="md"
          >
            <Icon as={Calendar} color="gray.500" boxSize={4} />
            <Text fontWeight="medium" fontSize="sm" color="gray.700">
              {currentYear}
            </Text>
          </HStack>
        </PopoverTrigger>
        <PopoverContent width="280px">
          <PopoverBody p={4}>
            <HStack justify="space-between" mb={4}>
              <IconButton
                aria-label="Decada anterior"
                icon={<Icon as={ChevronLeft} boxSize={4} />}
                onClick={() => handleDecadeChange('prev')}
                variant="ghost"
                size="sm"
              />
              <Text fontWeight="bold" fontSize="lg">
                {startYear} - {startYear + 11}
              </Text>
              <IconButton
                aria-label="Proxima decada"
                icon={<Icon as={ChevronRight} boxSize={4} />}
                onClick={() => handleDecadeChange('next')}
                variant="ghost"
                size="sm"
                isDisabled={startYear + 10 > currentYearNow}
              />
            </HStack>
            <Grid templateColumns="repeat(4, 1fr)" gap={2}>
              {years.map(year => (
                <Button
                  key={year}
                  size="sm"
                  variant={isYearSelected(year) ? 'solid' : 'ghost'}
                  colorScheme={isYearSelected(year) ? 'blue' : 'gray'}
                  isDisabled={isYearDisabled(year)}
                  onClick={() => onSelectYear(year)}
                  fontSize="xs"
                >
                  {year}
                </Button>
              ))}
            </Grid>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <IconButton
        aria-label="Proximo ano"
        icon={<Icon as={ChevronRight} />}
        onClick={onNextYear}
        variant="ghost"
        size="sm"
        colorScheme="blue"
        isDisabled={isCurrentYear}
      />
    </HStack>
  );
}

interface FilterFormValues {
  search: string;
  cnpj: string;
  companies: ICompany;
  cliente: IClient;
  cidade: ISelect<string>;
  uf: ISelect<string>;
  grupo_de_empresas: ISelect<string>;
  tags: ISelect<string>;
  comercial: ISelect<string>;
  contabil: ISelect<string>;
  financeiro: ISelect<string>;
  contas_a_pagar: ISelect<string>;
  encerramento_anual: ISelect<string>;
  fiscal: ISelect<string>;
  imposto_de_renda: ISelect<string>;
  integracao_contabil: ISelect<string>;
  integracao_fiscal: ISelect<string>;
  pessoal: ISelect<string>;
  relacionamento_com_cliente: ISelect<string>;
  societario: ISelect<string>;
}

type SearchMode = 'ID' | 'Nome' | 'CNPJ';

interface TaxRegimeFiltersProps {
  filters: URLSearchParams;
  currentYear: number;
  activeFiltersCount: number;
  searchMode: SearchMode;
  searchValue: string;
  viewMode: 'grid' | 'table';
  onViewModeChange: (mode: 'grid' | 'table') => void;
  onPrevYear: () => void;
  onNextYear: () => void;
  onSelectYear: (year: number) => void;
  onSearchModeChange: (mode: SearchMode) => void;
  onSearchChange: (search: string, mode: SearchMode) => void;
  onApplyFilters: (values: FilterFormValues) => void;
  onClearFilters: () => void;
}

export function TaxRegimeFilters({
  filters,
  currentYear,
  activeFiltersCount,
  searchMode,
  searchValue,
  viewMode,
  onViewModeChange,
  onPrevYear,
  onNextYear,
  onSelectYear,
  onSearchModeChange,
  onSearchChange,
  onApplyFilters,
  onClearFilters,
}: TaxRegimeFiltersProps) {
  const { getCompaniesFilters } = useCompanies();

  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();

  const filtersFormMethods = useForm<FilterFormValues>({
    mode: 'onChange',
    defaultValues: {
      search: searchValue,
      cnpj: filters.get('cnpj') ?? '',
    },
  });

  // Sincroniza o valor do campo de busca quando searchValue muda (ex: navegação por URL)
  useEffect(() => {
    const currentFormValue = filtersFormMethods.getValues('search');
    if (currentFormValue !== searchValue) {
      filtersFormMethods.setValue('search', searchValue);
    }
  }, [searchValue, filtersFormMethods]);

  const debouncedSearch = useDebounceCallback((value: string) => {
    onSearchChange(value, searchMode);
  }, 500);

  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods, debouncedSearch, searchMode]);

  const hasActiveFilters = activeFiltersCount > 0;

  const queryKeyCompaniesFilters = ['CompaniesFilters-taxRegime'];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  const handleApplyFiltersInternal = () => {
    const values = filtersFormMethods.getValues();
    onApplyFilters(values);
    onFilterDrawerClose();
  };

  const handleClearFiltersInternal = () => {
    onClearFilters();
    filtersFormMethods.reset({
      search: filters.get('search') || '',
    });
  };

  return (
    <Box mb={6} p={4} bg="gray.50" borderRadius="lg">
      <FormProvider {...filtersFormMethods}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={4}
        >
          <HStack spacing={0} flex={1} maxW={{ base: '100%', md: '350px' }}>
            <SearchModeBar
              modes={MODES_ID_NOME_CNPJ}
              mode={searchMode}
              onModeChange={m => onSearchModeChange(m as SearchMode)}
              value={filtersFormMethods.watch('search') ?? ''}
              onValueChange={v => filtersFormMethods.setValue('search', v, { shouldDirty: true })}
            />
          </HStack>

          <HStack spacing={3} flexWrap="wrap" justify={{ base: 'flex-start', md: 'flex-end' }}>
            <YearNavigator
              currentYear={currentYear}
              onPrevYear={onPrevYear}
              onNextYear={onNextYear}
              onSelectYear={onSelectYear}
            />
            <HStack
              spacing={1}
              bg="white"
              px={1}
              borderRadius="md"
              border="1px"
              borderColor="gray.200"
            >
              <IconButton
                aria-label="Visualização em tabela"
                icon={<Icon as={Table} />}
                onClick={() => onViewModeChange('table')}
                size="sm"
                variant={viewMode === 'table' ? 'solid' : 'ghost'}
                colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
              />
              <IconButton
                aria-label="Visualização em grid"
                icon={<Icon as={GridFour} />}
                onClick={() => onViewModeChange('grid')}
                size="sm"
                variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
              />
            </HStack>
            <Button
              onClick={onFilterDrawerOpen}
              leftIcon={<FunnelSimple size={16} />}
              variant="outline"
              colorScheme={hasActiveFilters ? 'blue' : 'gray'}
              size="sm"
            >
              Filtros{hasActiveFilters && ` (${activeFiltersCount})`}
            </Button>
          </HStack>
        </Flex>

        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={onFilterDrawerClose}
          title="Filtros Avancados"
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFiltersInternal}
          onApplyFilters={handleApplyFiltersInternal}
          formMethods={filtersFormMethods}
          size="lg"
        >
          <form className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SelectInputForm<ISelect<string>>
              name="cidade"
              placeholder="Filtrar por cidade"
              options={getFormattedLabelString(CompaniesFilters?.cidade)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="uf"
              placeholder="Filtrar por Estado"
              options={getFormattedLabelString(CompaniesFilters?.uf)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="grupo_de_empresas"
              placeholder="Filtrar por grupo de empresa"
              options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="tags"
              placeholder="Filtrar por tags"
              options={getFormattedLabelString(CompaniesFilters?.tags)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="comercial"
              placeholder="Filtrar por comercial"
              options={getFormattedLabelString(CompaniesFilters?.comercial)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="contabil"
              placeholder="Filtrar pelo contabil"
              options={getFormattedLabelString(CompaniesFilters?.contabil)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="financeiro"
              placeholder="Filtrar pelo financeiro"
              options={getFormattedLabelString(CompaniesFilters?.financeiro)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="contas_a_pagar"
              placeholder="Filtrar por contas a pagar"
              options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="encerramento_anual"
              placeholder="Filtrar por encerramento anual"
              options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="fiscal"
              placeholder="Filtrar por fiscal"
              options={getFormattedLabelString(CompaniesFilters?.fiscal)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="imposto_de_renda"
              placeholder="filtrar por imposto de renda"
              options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="integracao_contabil"
              placeholder="Filtrar por integracao contabil"
              options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="integracao_fiscal"
              placeholder="Filtrar por integracao fiscal"
              options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="pessoal"
              placeholder="Filtrar por pessoal"
              options={getFormattedLabelString(CompaniesFilters?.pessoal)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="relacionamento_com_cliente"
              placeholder="Filtrar por relacionamento com cliente"
              options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="societario"
              placeholder="Filtrar por societario"
              options={getFormattedLabelString(CompaniesFilters?.societario)}
              controlMb={0}
            />
          </form>
        </FilterDrawer>
      </FormProvider>
    </Box>
  );
}
